import { Box, Button, Container, Paper, Typography } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppContext } from "../../../../common/context/AppContext";
import { useBuyerSteps } from "../../../../common/hooks";
import { BuyerService } from "../../../../common/services/BuyerService";
import { HookedCheckbox } from "../../../components/HookedCheckbox";
import { HookedForm } from "../../../components/HookedForm";
import { HookedNumberField } from "../../../components/HookedNumberField";
import { HookedRadioGroup } from "../../../components/HookedRadioGroup";
import { BudgeOptions, ProfitOptions } from "../../../consts/consts";
import { BuyersStep, RoutePaths } from "../../../consts/enums";

const schema = yup
  .object({
    valuation: yup.number().min(10000).nullable().required().label("Budget"),
    debt_finance: yup.string().nullable().required().label("Debt Finance"),
    trailing_revenue: yup.string().nullable().required().label("TTM Revenue"),
    trailing_profit: yup
      .array()
      .min(1)
      .nullable()
      .required()
      .label("TTM Profit"),
  })
  .required();

export const BuyerBudget = () => {
  const { user, updateUser } = useAppContext();
  const { data, isLoading } = useBuyerSteps(BuyersStep.ACQUISITION_BUDGET);
  const formHook = useForm({
    defaultValues: data,
    resolver: yupResolver(schema),
  });
  const navigate = useNavigate();

  const handleNext = (values: any) => {
    BuyerService.PatchBuyer({ ...values });

    updateUser({ ...user, buyerFull: { ...user.buyerFull!, ...values } });
    navigate(RoutePaths.REGISTER_ACQUIRER + "/short-bio");
  };

  useEffect(() => {
    if (data && !isLoading) {
      formHook.reset({ ...data });
    }
  }, [data, isLoading]);

  return (
    <Box
      sx={{
        backgroundColor: "common.black",
        backgroundImage: "url(/images/BiggerGradient.png)",
        backgroundSize: "cover",
        backgroundPosition: "right",
        backgroundRepeat: "no-repeat",
        width: "100%",
        minHeight: "100%",
      }}
    >
      <Container maxWidth="md" sx={{ padding: 4 }}>
        <Typography variant="h3" sx={{ color: "white" }}>
          PUBLIC INFORMATION
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "common.grayOpacity" }}>
          Other Foundy users can view these details
        </Typography>
        <Paper
          variant="outlined"
          elevation={2}
          sx={{ marginTop: 4, padding: 3, position: "relative", zIndex: 1 }}
        >
          <HookedForm formHook={formHook}>
            <Typography variant="h3" sx={{ marginBottom: 1 }}>
              What is your total available budget for buying businesses?
            </Typography>
            <Typography
              variant="body2"
              sx={{ marginBottom: 2, color: "common.gray" }}
            >
              You may need to provide official confirmation of available funds
              or debt facility when speaking to sellers.
            </Typography>
            <HookedNumberField
              hiddenLabel
              fullWidth
              currency
              name="valuation"
              variant="filled"
              placeholder="It is important that this information is accurate so that we provide the best possible suggestions."
            />
            <Typography variant="h3" sx={{ marginBottom: 1, marginTop: 3 }}>
              Minimum twelve trailing month revenue you look for in businesses
              to buy
            </Typography>
            <Typography
              variant="body2"
              sx={{ marginBottom: 2, color: "common.gray" }}
            >
              Select one.
            </Typography>
            <HookedRadioGroup name="trailing_revenue" options={BudgeOptions} />

            <Typography variant="h3" sx={{ marginBottom: 2, marginTop: 3 }}>
              Are you interested in using debt finance to buy businesses?
            </Typography>
            <HookedRadioGroup
              name="debt_finance"
              options={[
                { label: "Yes", value: "YES" },
                { label: "No", value: "NO" },
                { label: "Undecided", value: "UNDECIDED" },
              ]}
            />
            <Typography variant="h3" sx={{ marginBottom: 1, marginTop: 3 }}>
              Minimum twelve trailing month profit you look for in businesses to
              buy
            </Typography>
            <Typography
              variant="body2"
              sx={{ marginBottom: 2, color: "common.gray" }}
            >
              Select one, and the last option if it applies to you.
            </Typography>
            <HookedCheckbox name="trailing_profit" options={ProfitOptions} />
          </HookedForm>
        </Paper>
        <Box
          sx={{
            textAlign: "right",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() =>
              navigate(RoutePaths.REGISTER_ACQUIRER + "/locations")
            }
            sx={{ marginTop: 2 }}
            variant="roundsecondary"
          >
            Back
          </Button>
          <Button
            onClick={formHook.handleSubmit(handleNext)}
            sx={{ marginTop: 2 }}
            variant="roundgradient"
          >
            Save &amp; Continue
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
